import { ChevronLeft, ChevronRight } from 'lucide-react'

export const PAGE_SIZE_OPTIONS = [10, 15, 25, 50]

export function PlannerPagination({
  page,
  pageSize,
  total,
  summarySuffix,
  onPageChange,
  onPageSizeChange,
}: {
  page: number
  pageSize: number
  total: number
  /** Extra text after the range, e.g. "· 29 types". */
  summarySuffix?: string
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const currentPage = Math.min(page, totalPages)
  const start = (currentPage - 1) * pageSize
  const rangeStart = total === 0 ? 0 : start + 1
  const rangeEnd = Math.min(start + pageSize, total)

  return (
    <div className='mt-4 flex flex-col gap-3 border-t border-gray-100 pt-3 sm:flex-row sm:items-center sm:justify-between'>
      <div className='flex items-center gap-3 text-xs text-gray-500'>
        <span>
          Showing <span className='font-medium text-gray-700'>{rangeStart}–{rangeEnd}</span> of{' '}
          <span className='font-medium text-gray-700'>{total}</span>
          {summarySuffix ? ` ${summarySuffix}` : ''}
        </span>
        <span className='hidden items-center gap-1.5 md:flex'>
          <span className='text-gray-300'>|</span>
          <label htmlFor='planner-page-size'>Rows</label>
          <select
            id='planner-page-size'
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className='h-7 rounded-md border border-gray-200 bg-white px-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring'
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </span>
      </div>

      <div className='flex items-center gap-1'>
        <button
          type='button'
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className='inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'
          aria-label='Previous page'
        >
          <ChevronLeft className='h-4 w-4' />
        </button>
        <span className='px-2 text-xs font-medium text-gray-600'>
          Page {currentPage} of {totalPages}
        </span>
        <button
          type='button'
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className='inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'
          aria-label='Next page'
        >
          <ChevronRight className='h-4 w-4' />
        </button>
      </div>
    </div>
  )
}
